import React, { Component, createRef } from "react";
import {
    Button,
    Form,
    FormGroup,
    Card,
    CardBody,
    CardHeader,
    CardFooter,
} from "reactstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import { API_URL } from "helpers/app_url";
import SafeField from "helpers/SafeField";

const ComposeSchema = Yup.object().shape({
    to: Yup.string().email("Invalid email").required("Recipient email is required"),
    subject: Yup.string().required("Subject is required"),
});

const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
];

export default class ComposeMailForword extends Component {
    constructor(props) {
        super(props);
        this.state = {
            attachments: [],
            showCcBcc: false,
            isMinimized: false,
            templates: [],
        };

        this.bodyRef = createRef();
        this.fileInputRef = createRef();
    }

    componentDidMount() {
        if (this.bodyRef.current) this.bodyRef.current.focus();
        this.fetchAllTemplates();

        // Insert quoted original message
        if (this.props.originalEmail) {
            this.insertForwardQuote(this.props.originalEmail);
        }
    }

    fetchAllTemplates() {
        const obj = JSON.parse(localStorage.getItem("authUser"));
        const token = obj?.resultData?.bearerToken;

        axios
            .get(`${API_URL}AgentCustomEmailTemplate/allAgent`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { AgentId: localStorage.getItem("userId") },
            })
            .then((response) => {
                this.setState({ templates: response.data });
            })
            .catch((error) => {
                this.setState({ error: error.message });
            });
    }

   insertForwardQuote = (email) => {
    const date = moment(email.createdOn).format("MMM DD, YYYY h:mm A");

    const quotedHtml = `
        <br><br>
        ---------- Forwarded message ----------<br>
        <strong>From:</strong> ${email.from}<br>
        <strong>Date:</strong> ${date}<br>
        <strong>Subject:</strong> ${email.subject}<br>
        <strong>To:</strong> ${email.to}<br><br>

        ${email.message}
    `;

    if (this.bodyRef.current) {
        this.bodyRef.current.innerHTML = quotedHtml;
    }
};
    handleAttachmentFiles = (files) => {
        const safeFiles = Array.from(files).filter((file) => {
            if (ALLOWED_FILE_TYPES.includes(file.type)) return true;
            alert(`"${file.name}" is not allowed.`);
            return false;
        });

        const arr = safeFiles.map((file) => {
            const isImage = file.type.startsWith("image/");
            return {
                id: Date.now() + Math.random(),
                file,
                previewUrl: isImage ? URL.createObjectURL(file) : null,
                isImage,
            };
        });

        this.setState((prev) => ({
            attachments: [...prev.attachments, ...arr],
        }));
    };

    handleFileChange = (e) => {
        this.handleAttachmentFiles(e.target.files);
        e.target.value = null;
    };

    removeAttachment = (id) => {
        this.setState((prev) => ({
            attachments: prev.attachments.filter((a) => a.id !== id),
        }));
    };

    applyFormat = (cmd, value = null) => {
        document.execCommand(cmd, false, value);
        this.bodyRef.current && this.bodyRef.current.focus();
    };

    handleDiscard = () => {
        if (!window.confirm("Discard draft?")) return;
        this.setState({ attachments: [] });
        if (this.bodyRef.current) this.bodyRef.current.innerHTML = "";
        if (this.props.onClose) this.props.onClose();
    };

    handleTemplateSelect = (e, setFieldValue) => {
        const id = e.target.value;
        if (!id) return;

        const token = JSON.parse(localStorage.getItem("authUser"))?.resultData
            ?.bearerToken;

        axios
            .get(`${API_URL}AgentCustomEmailTemplate/GetAgentCustomEmailTemplateByGuid`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { id },
            })
            .then((response) => {
                const template = response.data;
                if (!template) return toast.warn("Template not found!");

                setFieldValue("subject", template.templateEmailSubject || "");
                if (this.bodyRef.current)
                    this.bodyRef.current.innerHTML = template.templateEmailBody || "";
            })
            .catch(() => toast.error("Failed to load template"));
    };

    render() {
        const { attachments, showCcBcc, isMinimized, templates } = this.state;
        const { agentData, originalEmail } = this.props;

        return (
            <div
                className="position-fixed bottom-0 end-0 mb-3 me-3"
                style={{ width: "820px", zIndex: 1050 }}
            >
                <Card className="shadow-lg rounded-4 border-0 overflow-hidden">
                    <CardHeader className="d-flex justify-content-between align-items-center bg-warning text-white py-2 px-3">
                        <h6 className="mb-0">Forward Message</h6>

                        <div>
                            <Button
                                color="link"
                                size="sm"
                                className="text-white me-2"
                                onClick={() =>
                                    this.setState({ isMinimized: !isMinimized })
                                }
                            >
                                {isMinimized ? "⬆️" : "➖"}
                            </Button>

                            <Button
                                color="link"
                                size="sm"
                                className="text-white"
                                onClick={this.handleDiscard}
                            >
                                ✖️
                            </Button>
                        </div>
                    </CardHeader>

                    {!isMinimized && (
                        <Formik
                            initialValues={{
                                to:  "",
                                cc: "",
                                bcc: "",
                                subject: originalEmail?.subject?.startsWith("Fwd:")
                                    ? originalEmail.subject
                                    : "Fwd: " + originalEmail.subject,
                                agentId: agentData?.agentId || "",
                            }}
                            validationSchema={ComposeSchema}
                            onSubmit={(values, { setSubmitting, resetForm }) => {
                                const bodyHtml = this.bodyRef.current.innerHTML;

                                const token = JSON.parse(
                                    localStorage.getItem("authUser")
                                )?.resultData?.bearerToken;

                                const formData = new FormData();
                                formData.append("to", values.to);
                                formData.append("cc", values.cc);
                                formData.append("bcc", values.bcc);
                                formData.append("subject", values.subject);
                                formData.append("body", bodyHtml);
                                formData.append("agentId", values.agentId);

                                this.state.attachments.forEach((a) =>
                                    formData.append("files", a.file, a.file.name)
                                );

                                toast.loading("Sending...");

                                axios
                                    .post(`${API_URL}Gmail`, formData, {
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            "Content-Type": "multipart/form-data",
                                        },
                                    })
                                    .then(() => {
                                        toast.dismiss();
                                        toast.success("Email sent!");
                                        resetForm();
                                        if (this.bodyRef.current)
                                            this.bodyRef.current.innerHTML = "";
                                        if (this.props.onClose) this.props.onClose();
                                    })
                                    .catch(() => {
                                        toast.dismiss();
                                        toast.error("Failed to send email");
                                    })
                                    .finally(() => setSubmitting(false));
                            }}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <>
                                    <CardBody
                                        className="p-3"
                                        style={{
                                            maxHeight: "70vh",
                                            overflowY: "auto",
                                        }}
                                    >
                                        <Form onSubmit={handleSubmit}>
                                            {/* TO */}
                                            <FormGroup className="mb-2">
                                                <SafeField
                                                    type="text"
                                                    name="to"
                                                    placeholder="To"
                                                    value={values.to}
                                                    
                                                    className={`form-control ${
                                                        errors.to && touched.to
                                                            ? "is-invalid"
                                                            : ""
                                                    }`}
                                                />
                                            </FormGroup>

                                            {/* CC / BCC */}
                                            {showCcBcc && (
                                                <>
                                                    <FormGroup className="mb-2">
                                                        <SafeField
                                                            type="text"
                                                            name="cc"
                                                            placeholder="Cc"
                                                            onChange={handleChange}
                                                            className="form-control"
                                                        />
                                                    </FormGroup>

                                                    <FormGroup className="mb-2">
                                                        <SafeField
                                                            type="text"
                                                            name="bcc"
                                                            placeholder="Bcc"
                                                            onChange={handleChange}
                                                            className="form-control"
                                                        />
                                                    </FormGroup>
                                                </>
                                            )}

                                            {!showCcBcc && (
                                                <div
                                                    className="mb-2 text-primary small cursor-pointer"
                                                    onClick={() =>
                                                        this.setState({
                                                            showCcBcc: true,
                                                        })
                                                    }
                                                >
                                                    Add Cc/Bcc
                                                </div>
                                            )}

                                            {/* SUBJECT */}
                                            <FormGroup className="mb-2">
                                                <SafeField
                                                    type="text"
                                                    name="subject"
                                                    value={values.subject}
                                                    
                                                    className="form-control"
                                                />
                                            </FormGroup>

                                            {/* Toolbar */}
                                            <div className="border rounded bg-light p-1 mb-2 d-flex gap-1 align-items-center">
                                                <Button
                                                    size="sm"
                                                    color="light"
                                                    onClick={() =>
                                                        this.applyFormat("bold")
                                                    }
                                                >
                                                    <b>B</b>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="light"
                                                    onClick={() =>
                                                        this.applyFormat("italic")
                                                    }
                                                >
                                                    <i>I</i>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="light"
                                                    onClick={() =>
                                                        this.applyFormat(
                                                            "underline"
                                                        )
                                                    }
                                                >
                                                    <u>U</u>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="light"
                                                    onClick={() =>
                                                        this.applyFormat(
                                                            "insertUnorderedList"
                                                        )
                                                    }
                                                >
                                                    • List
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="light"
                                                    onClick={() =>
                                                        this.applyFormat(
                                                            "insertOrderedList"
                                                        )
                                                    }
                                                >
                                                    1. List
                                                </Button>

                                                <div className="ms-auto d-flex gap-3 align-items-center">
                                                    <select
                                                        className="form-select"
                                                        onChange={(e) =>
                                                            this.handleTemplateSelect(
                                                                e,
                                                                setFieldValue
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Select Template
                                                        </option>

                                                        {templates?.map(
                                                            (t, i) => (
                                                                <option
                                                                    key={i}
                                                                    value={
                                                                        t.templateId
                                                                    }
                                                                >
                                                                    {
                                                                        t.templateTitle
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </select>

                                                    <Button
                                                        outline
                                                        size="sm"
                                                        color="dark"
                                                        onClick={() =>
                                                            this.fileInputRef.current.click()
                                                        }
                                                    >
                                                        📎 Attach
                                                    </Button>

                                                    <input
                                                        type="file"
                                                        ref={this.fileInputRef}
                                                        multiple
                                                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                                        className="d-none"
                                                        onChange={
                                                            this.handleFileChange
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {/* BODY */}
                                            <div
                                                ref={this.bodyRef}
                                                contentEditable
                                                suppressContentEditableWarning
                                                className="border rounded p-2 mb-3"
                                                style={{
                                                    minHeight: "250px",
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            ></div>

                                            {/* ATTACHMENTS */}
                                            {attachments.length > 0 && (
                                                <div className="border rounded p-2 bg-light mb-2">
                                                    <h6 className="small mb-2">
                                                        Attachments
                                                    </h6>

                                                    <div className="d-flex flex-wrap gap-2">
                                                        {attachments.map((a) => (
                                                            <div
                                                                key={a.id}
                                                                className="position-relative border rounded p-2 bg-white text-center"
                                                                style={{
                                                                    width: "100px",
                                                                    height: "100px",
                                                                    overflow: "hidden",
                                                                }}
                                                            >
                                                                {a.isImage ? (
                                                                    <img
                                                                        src={
                                                                            a.previewUrl
                                                                        }
                                                                        alt={
                                                                            a.file
                                                                                .name
                                                                        }
                                                                        style={{
                                                                            width: "80px",
                                                                            height: "80px",
                                                                            objectFit:
                                                                                "cover",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div>
                                                                        {a.file.type.includes(
                                                                            "pdf"
                                                                        ) && "📕"}
                                                                        {a.file.type.includes(
                                                                            "word"
                                                                        ) && "📘"}
                                                                        {a.file.type.includes(
                                                                            "excel"
                                                                        ) && "📊"}
                                                                        {a.file.type.includes(
                                                                            "text"
                                                                        ) && "📄"}

                                                                        <div className="small text-truncate">
                                                                            {a.file.name}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <Button
                                                                    close
                                                                    className="position-absolute top-0 end-0"
                                                                    onClick={() =>
                                                                        this.removeAttachment(
                                                                            a.id
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Form>
                                    </CardBody>

                                    <CardFooter className="bg-light py-2 px-3 d-flex justify-content-between">
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={handleSubmit}
                                        >
                                            Send
                                        </Button>
                                    </CardFooter>
                                </>
                            )}
                        </Formik>
                    )}
                </Card>
            </div>
        );
    }
}

ComposeMailForword.propTypes = {
    onSend: PropTypes.func,
    onClose: PropTypes.func,
    agentData: PropTypes.object,
    originalEmail: PropTypes.object,
};
